// Nutrition › Recettes: search, filters Rapide / Végétarien / Favoris (UBM ChipGroup), featured card + list; and the recipe
// page (#/nutrition?r=<rubrique>&recette=<id>, the format used by ⌘K): portions (UBM NumberField), Ingrédients / Préparation
// (UBM SegmentedControl), « Ajouter aux repas » (drawer) and « Ajouter aux courses » (visible at once in Courses).
import { Button } from "@ubm/ui/components/button";
import { useState } from "react";
import { ArrowRight, Check, Clock, Heart, Leaf, Plus, ShoppingBasket } from "lucide-react";
import { ChipGroup } from "@ubm/ui/components/chip-group";
import { NumberField } from "@ubm/ui/components/number-field";
import { SearchField } from "@ubm/ui/components/search-field";
import { SegmentedControl } from "@ubm/ui/components/segmented-control";
import { DAYS, FEATURED, MEALS, MEAL_LABEL, QUICK_MINUTES, RECIPES, type MealId, type Recipe } from "./nutrition-fixtures";
import { DEMO_TODAY_KEY } from "./demo-context";
import { NutriSheet, Thumb, fmtAmount, nutri, recipeHref, recipeOf, useNutri } from "./nutrition-parts";

let savedQuery = "";
let savedFilters: string[] = [];

function Fav({ id, on, className }: { id: string; on: boolean; className: string }) {
  const name = recipeOf(id).name;
  return <button type="button" className={className} aria-pressed={on} aria-label={on ? `Retirer ${name} des favoris` : `Ajouter ${name} aux favoris`}
    onClick={() => { nutri.toggleFav(id); nutri.toast(on ? "Retiré des favoris" : "Ajouté aux favoris"); }}>
    <Heart size={18} strokeWidth={2} fill={on ? "currentColor" : "none"} />
  </button>;
}

export function NutritionRecettes() {
  const s = useNutri();
  const [q, setQState] = useState(savedQuery);
  const setQ = (v: string) => { savedQuery = v; setQState(v); };
  const [filters, setFiltersState] = useState<string[]>(savedFilters);
  const setFilters = (v: string[]) => { savedFilters = v; setFiltersState(v); };
  const needle = q.trim().toLowerCase();
  const list = RECIPES.filter(r => (!filters.includes("rapide") || r.minutes <= QUICK_MINUTES)
    && (!filters.includes("vegetarien") || r.vegetarian) && (!filters.includes("favoris") || s.favs.includes(r.id))
    && (!needle || r.name.toLowerCase().includes(needle) || r.ingredients.some(i => i.name.toLowerCase().includes(needle))));
  const featured = !needle && !filters.length ? RECIPES.find(r => r.id === FEATURED && r.photo) : undefined;
  const rest = list.filter(r => r !== featured);

  return <>
    <div className="ccn-search"><SearchField label="Rechercher une recette" placeholder="Une recette, un ingrédient" value={q} onValueChange={setQ} /></div>
    <ChipGroup className="ccn-filters" label="Filtrer les recettes" value={filters} onValueChange={setFilters}
      options={[{ value: "rapide", label: "Rapide" }, { value: "vegetarien", label: "Végétarien" }, { value: "favoris", label: "Favoris" }]} />

    {featured && <div className="ccn-feature">
      <a className="ccn-feature-link" href={recipeHref("recettes", featured.id)}>
        <img className="ccn-feature-img" src={featured.photo} alt="" />
        <span className="ccn-feature-shade" aria-hidden="true" />
        <span className="ccn-feature-pill"><Clock size={13} strokeWidth={2.2} /> {featured.minutes} min</span>
        <span className="ccn-feature-text"><span className="ccn-over">La recette de la semaine</span><b>{featured.name}</b><span className="ccn-feature-cta">Voir la recette <ArrowRight size={15} strokeWidth={2.2} /></span></span>
      </a>
      <Fav id={featured.id} on={s.favs.includes(featured.id)} className="ccn-fav ccn-fav--float" />
    </div>}

    <div className="ccn-sechead"><h2 className="ccn-h2">{featured ? "Autres recettes" : `${list.length} recette${list.length > 1 ? "s" : ""}`}</h2></div>
    {rest.length ? <ul className="ccn-reclist">{rest.map(r => <li key={r.id} className="ccn-recrow">
      <a className="ccn-recrow-link" href={recipeHref("recettes", r.id)}>
        <Thumb photo={r.photo} className="ccn-recrow-img" />
        <span className="ccn-recrow-text"><b>{r.name}</b><small><Clock size={12} strokeWidth={2.2} /> {r.minutes} min{r.vegetarian ? <> · <Leaf size={12} strokeWidth={2.2} /> Végétarien</> : null}</small></span>
      </a>
      <Fav id={r.id} on={s.favs.includes(r.id)} className="ccn-fav" />
    </li>)}</ul>
      : !featured && <div className="ccn-empty"><p>Aucune recette ne correspond.</p><button type="button" className="ccn-textbtn" onClick={() => { setQ(""); setFilters([]); }}>Effacer la recherche et les filtres</button></div>}
  </>;
}

let savedTab: "ing" | "prep" = "ing";

export function RecipeDetail({ recipe: r }: { recipe: Recipe }) {
  const s = useNutri();
  const [portions, setPortions] = useState(s.extras[r.id] ?? r.portions);
  const [tab, setTabState] = useState(savedTab);
  const setTab = (v: "ing" | "prep") => { savedTab = v; setTabState(v); };
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState<{ day: string; meal: MealId }>({ day: DEMO_TODAY_KEY, meal: "dejeuner" });
  const k = portions / r.portions;
  const inCourses = s.extras[r.id];

  return <article className="ccn-recipe">
    <div className="ccn-recipe-hero"><Thumb photo={r.photo} className="ccn-recipe-img" /><Fav id={r.id} on={s.favs.includes(r.id)} className="ccn-fav ccn-fav--float" /></div>
    <h2 className="ccn-recipe-title">{r.name}</h2>
    <p className="ccn-recipe-meta"><span><Clock size={14} strokeWidth={2.2} /> {r.minutes} min</span>{r.vegetarian && <span><Leaf size={14} strokeWidth={2.2} /> Végétarien</span>}{r.category !== "Végétarien" && <span>{r.category}</span>}</p>
    <div className="ccn-recipe-portions"><NumberField label="Portions" value={portions} min={1} max={8} locale="fr-FR" size="sm" onValueChange={setPortions} /></div>

    <SegmentedControl className="ccn-seg" label="Contenu de la recette" value={tab} onValueChange={v => setTab(v as "ing" | "prep")}
      options={[{ value: "ing", label: "Ingrédients" }, { value: "prep", label: "Préparation" }]} />
    {tab === "ing"
      ? <ul className="ccn-ing">{r.ingredients.map(i => <li key={i.name}><span>{i.name}</span><b>{fmtAmount(i.qty * k, i.unit)}</b></li>)}</ul>
      : <ol className="ccn-steps">{r.steps.map((x, n) => <li key={x}><span aria-hidden="true">{n + 1}</span><p>{x}</p></li>)}</ol>}

    <div className="ccn-recipe-actions">
      <Button variant="primary" size="lg" className="cc-cta" onClick={() => setAdding(true)}><Plus size={17} strokeWidth={2.4} /> Ajouter aux repas</Button>
      {inCourses === portions
        ? <a className="ccn-outline ccn-outline--wide" href="#/nutrition?r=courses"><Check size={16} strokeWidth={2.6} /> Dans mes courses · Voir la liste</a>
        : <Button variant="secondary" size="sm" className="ccn-outline--wide" onClick={() => {
            nutri.set({ extras: { ...s.extras, [r.id]: portions } });
            nutri.toast(inCourses ? "Courses mises à jour" : `${r.ingredients.length} ingrédients ajoutés aux courses`);
          }}><ShoppingBasket size={16} strokeWidth={2} /> {inCourses ? `Mettre à jour mes courses (${portions} portion${portions > 1 ? "s" : ""})` : "Ajouter aux courses"}</Button>}
    </div>

    {adding && <NutriSheet title="Ajouter aux repas" onClose={() => setAdding(false)}>
      <p className="ccn-sheet-sub">{r.name} · {portions} portion{portions > 1 ? "s" : ""}</p>
      <p className="ccn-sheet-label">Jour</p>
      <div className="ccn-choice">{DAYS.map(d => <button key={d.key} type="button" className="cc-chip" aria-pressed={draft.day === d.key} data-active={draft.day === d.key || undefined} onClick={() => setDraft(x => ({ ...x, day: d.key }))}>{d.letter} {d.num}</button>)}</div>
      <p className="ccn-sheet-label">Repas</p>
      <div className="ccn-choice">{MEALS.map(m => <button key={m} type="button" className="cc-chip" aria-pressed={draft.meal === m} data-active={draft.meal === m || undefined} onClick={() => setDraft(x => ({ ...x, meal: m }))}>{MEAL_LABEL[m]}</button>)}</div>
      {s.plan[draft.day]?.[draft.meal] && <p className="ccn-sheet-sub">Ce repas remplacera celui déjà prévu.</p>}
      <Button variant="primary" size="lg" className="cc-cta" onClick={() => {
        nutri.setEntry(draft.day, draft.meal, { recipe: r.id, portions });
        setAdding(false);
        nutri.toast(`Ajouté · ${DAYS.find(d => d.key === draft.day)!.long.split(" ")[0]} · ${MEAL_LABEL[draft.meal]}`);
      }}>Ajouter</Button>
    </NutriSheet>}
  </article>;
}
